import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    q: 'What are the minimum qualifications?',
    a: 'The only hard requirement is $10,000 in monthly business revenue. We work with US-based businesses across nearly every industry. Personal credit is reviewed but rarely the deciding factor — we underwrite the business, not the founder.',
  },
  {
    q: 'How fast can I actually be funded?',
    a: 'Merchant cash advance, business line of credit, and invoice factoring typically close in 24–72 hours from a completed intake. Equipment financing settles in 3–5 business days; HELOC in 10–14 days; SBA in 30–45 days. Speed depends on how quickly you can share bank statements and entity documents.',
  },
  {
    q: 'Do you require collateral?',
    a: 'Merchant cash advance and business line of credit are typically unsecured against personal assets. Equipment financing and HELOC are asset-backed by definition. Invoice factoring is secured against the invoices themselves. We never require personal residence collateral for operating capital.',
  },
  {
    q: 'What are your rates?',
    a: 'Rates range from prime + 0% (HELOC, qualified borrowers) and 8.99% (SBA structured) to a factored equivalent for merchant cash advance and invoice factoring. Every file is priced against risk, revenue, and structure — not a rate card.',
  },
  {
    q: 'Is there any fee to apply?',
    a: 'No. Application, underwriting review, and offer are always free. You only sign when the terms are right for your business.',
  },
  {
    q: 'How do you protect confidentiality?',
    a: 'Every intake is encrypted end-to-end. Files are worked by two principals maximum, never shared with committees or third parties without written consent, and shredded post-close.',
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      className="relative bg-[#050505] py-28 lg:py-40 border-t border-[#c2a978]/10"
      data-testid="faq-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 lg:mb-28">
          <div className="lg:col-span-4 flex items-start gap-4">
            <span className="w-10 h-px bg-[#c2a978] mt-3" />
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#c2a978]">
              Ch. 06 — Frictions
            </span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-8 font-serif text-3xl lg:text-5xl leading-[0.95] tracking-[-0.02em]"
          >
            Questions,<br />
            <span className="italic text-[#c2a978]">answered plainly</span>.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-10 lg:col-start-3">
            <div className="border-t border-[#c2a978]/15">
              {faqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={f.q}
                    className="border-b border-[#c2a978]/15"
                    data-testid={`faq-item-${i}`}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="w-full flex items-center justify-between gap-8 py-6 lg:py-8 text-left group"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="flex items-baseline gap-6 lg:gap-10">
                        <span className="font-serif italic text-[#c2a978] text-lg lg:text-2xl">
                          0{i + 1}
                        </span>
                        <span
                          className={`font-serif text-lg lg:text-xl leading-tight transition-colors duration-300 ${
                            isOpen ? 'text-[#c2a978]' : 'text-[#f5f5f0] group-hover:text-[#c2a978]'
                          }`}
                        >
                          {f.q}
                        </span>
                      </span>
                      <span className="text-[#c2a978] shrink-0">
                        {isOpen ? <Minus size={24} strokeWidth={1} /> : <Plus size={24} strokeWidth={1} />}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pb-8 lg:pb-10 pl-10 lg:pl-24 pr-4 max-w-3xl">
                            <p className="text-[#8a8a8a] text-sm lg:text-base leading-relaxed">
                              {f.a}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
